/* eslint-disable no-param-reassign */

import type { CloudBlueProductType } from "@onestore/onestore-store-common/api/shapes"
import {
  convertPeriodToBonusPeriods,
  getBonusResourceSetup,
  getDefinition,
  getDomainDefinition,
  getUpsellDefinitions,
} from "@gatsby-plugin-bonus/lib/bonus-definitions"
import { getParamsOfType } from "@gatsby-plugin-bonus/lib/query-parser"
import type { Draft } from "immer"
import { produce } from "immer"
import get from "lodash/get"
import { isCloudBluePlanUpsell } from "~/helpers/isCloudBluePlanUpsell"
import isEmpty from "~/lib/isEmpty"
import type {
  AxDrawerCloseAction,
  AxDrawerDefinitionsLoadedAction,
  AxDrawerShowAction,
  AxDrawerToggleBundleAction,
  AxDrawerToggleUpsellAction,
} from "./actions-ax-drawer"
import type { BonusBundleActions } from "./actions-bundle"
import type {
  BackToShopUrlSetAction,
  MiniCartCloseAction,
  MiniCartOpenAction,
} from "./actions-mini-cart"
import {
  BASKET_UPSELL_DEFINITION_GET_SUCCESS,
  type BasketUpsellDefinitionGetSuccessAction,
  PRODUCT_DEFINITIONS_GET_SUCCESS,
  type ProductDefinitionActions,
} from "./actions-product"
import { BonusActions } from "./constants"
import { initialState } from "./selectors"
import type { BonusProduct, BonusState } from "./types"
import {
  defaultCheckedResources,
  getBonusItemTotalPrices,
  getInitialChosenPeriod,
  getProductParameters,
  getResourceLabels,
} from "./utils"

function handleDefinitionGet(
  product: CloudBlueProductType | null | undefined,
  draftProduct: Draft<BonusProduct>
): void {
  if (!product || !draftProduct) {
    return
  }
  const { periods } = product

  draftProduct.resourceLabels = getResourceLabels(product.resources || {})
  draftProduct.periods = convertPeriodToBonusPeriods(
    periods,
    product.min_quantity ?? 1
  )

  draftProduct.chosenPeriodId = getInitialChosenPeriod(
    draftProduct.periods,
    draftProduct?.chosenPeriodId,
    draftProduct.defaultPeriodName
  )

  if (draftProduct.advancedConfiguration) {
    draftProduct.resources = defaultCheckedResources(
      draftProduct.resourceCategories,
      draftProduct.chosenPeriodId,
      draftProduct.periods,
      draftProduct?.resources
    )

    draftProduct.parameters = getProductParameters(product.parameters)
  }
  draftProduct.totalPrices = getBonusItemTotalPrices(product, draftProduct)
}

export default function reducer(
  state: BonusState = initialState,
  action:
    | BonusBundleActions
    | ProductDefinitionActions
    | BasketUpsellDefinitionGetSuccessAction
    | AxDrawerShowAction
    | AxDrawerCloseAction
    | AxDrawerDefinitionsLoadedAction
    | AxDrawerToggleUpsellAction
    | AxDrawerToggleBundleAction
    | MiniCartOpenAction
    | MiniCartCloseAction
    | BackToShopUrlSetAction
) {
  return produce(state, (nextState: Draft<BonusState>) => {
    switch (action.type) {
      case PRODUCT_DEFINITIONS_GET_SUCCESS: {
        const parsedData = action.config

        nextState.calculation = {
          bundled: undefined,
          product: undefined,
        }

        nextState.upsellDomains = {
          results: [],
          status: 0,
        }

        nextState.forms = {}
        nextState.special = parsedData.special
        nextState.promoCode = parsedData.promoCode
        nextState.chosenPeriodName = parsedData.chosenPeriod
        nextState.hidePeriods = parsedData.hidePeriods
        nextState.checkoutMode = parsedData.checkoutMode
        nextState.domainSearch = parsedData.domainSearch
        nextState.loaded = true

        const productData = action.definitions.find(
          (item) => item.alias === parsedData.product?.alias
        )
        const bundledProductData = parsedData.bundled
          ? action.definitions.find(
              (item) => item.alias === parsedData.bundled!.alias
            )
          : null

        nextState.upsell = parsedData.upsell
          ? getUpsellDefinitions(
              parsedData.upsell,
              parsedData.allPeriod || null,
              action.definitions
            )
          : {}

        if (parsedData.product) {
          const productMinQuantity =
            parsedData.product.parameters &&
            "quantity" in parsedData.product.parameters
              ? parseInt(parsedData.product.parameters.quantity, 10)
              : null

          const productPeriod =
            parsedData.allPeriod ?? (parsedData.chosenPeriod || null)

          const productDefinition = getDefinition(
            productData,
            productPeriod,
            null,
            productMinQuantity
          )

          if (productDefinition) {
            nextState.product = productDefinition
            handleDefinitionGet(productData, nextState.product)

            if (parsedData.product && parsedData.product.parameters) {
              nextState.product.resources = getBonusResourceSetup(
                parsedData.product.parameters
              )

              nextState.product.initialParamFormData = getParamsOfType(
                "param",
                parsedData.product.parameters
              )
            }
          }

          if (nextState.upsell) {
            delete nextState.upsell[parsedData.product.alias]
          }
        }

        if (parsedData.domain) {
          const domainDefinition = getDomainDefinition(parsedData.domain)

          if (domainDefinition) {
            nextState.domain = domainDefinition
          }
        }

        if (parsedData.bundled) {
          const bundledDefinition = getDefinition(
            bundledProductData,
            parsedData.allPeriod || null,
            productData,
            null
          )

          if (bundledDefinition) {
            nextState.bundled = bundledDefinition

            if (nextState.upsell) {
              delete nextState.upsell[parsedData.bundled.alias]
            }
          }
        }

        parsedData.upsell?.forEach((item) => {
          const product = action.definitions.find(
            (def) => def.alias === item.alias
          )
          handleDefinitionGet(product, nextState.upsell[item.alias])
        })

        return
      }

      case BASKET_UPSELL_DEFINITION_GET_SUCCESS: {
        const definitionsMap = getUpsellDefinitions(
          [{ alias: action.alias }],
          null,
          action.definitions
        )

        nextState.upsell = { ...nextState.upsell, ...definitionsMap }

        const upsellProduct = nextState.upsell[action.alias]

        if (upsellProduct) {
          const remoteProduct = action.definitions.find(
            (definition) => definition.alias === action.alias
          )
          handleDefinitionGet(remoteProduct, upsellProduct)
        }

        return
      }

      case BonusActions.AX_DRAWER_SHOW: {
        nextState.axDrawer = {
          saleConfiguration: action.saleConfiguration,
          bundle: action.bundle,
          // Carried separately from `bundle`: the row is shown whenever there
          // is a bundle, checked or not.
          isBundleSelectedInDrawer: action.isBundleSelected,
        }
        return
      }

      case BonusActions.AX_DRAWER_TOGGLE_BUNDLE: {
        if (!nextState.axDrawer) {
          return
        }

        nextState.axDrawer.isBundleSelectedInDrawer =
          !nextState.axDrawer.isBundleSelectedInDrawer

        return
      }

      case BonusActions.AX_DRAWER_DEFINITIONS_LOADED: {
        const { definitions, saleConfiguration } = action

        if (nextState.axDrawer?.saleConfiguration.id !== saleConfiguration.id) {
          return
        }

        const mainPlan = get(
          saleConfiguration,
          "flatData.defaultCloudBluePlan[0]",
          null
        )

        const defaultPeriod =
          saleConfiguration.flatData.defaultPlanPeriod?.period ?? null

        if (!isEmpty(mainPlan)) {
          const mainDefinition = definitions.find(
            (definition) => definition.alias === mainPlan.flatData.alias
          )

          const productDefinition = getDefinition(mainDefinition, defaultPeriod)

          if (productDefinition) {
            nextState.product = productDefinition
            handleDefinitionGet(mainDefinition, nextState.product)
          }
        }

        const bundlePlan =
          nextState.axDrawer?.bundle?.flatData.plan?.[0] ?? null

        if (!isEmpty(bundlePlan) && nextState.axDrawer) {
          const bundleDefinition = definitions.find(
            (definition) => definition.alias === bundlePlan.flatData.alias
          )

          const bundleProductDefinition = getDefinition(
            bundleDefinition,
            defaultPeriod
          )

          if (bundleProductDefinition) {
            nextState.axDrawer.bundleProduct = bundleProductDefinition

            handleDefinitionGet(
              bundleDefinition,
              nextState.axDrawer.bundleProduct
            )
          }
        }

        const upsellPlans = (saleConfiguration.flatData.upsell ?? []).filter(
          isCloudBluePlanUpsell
        )

        nextState.upsell = {}

        for (const plan of upsellPlans) {
          const currentUpsellDefinition = definitions.find(
            (definition) => definition.alias === plan.flatData.alias
          )

          const upsellDefinition = getDefinition(
            currentUpsellDefinition,
            plan.flatData.upsellDefaultPeriod?.period ?? null
          )

          if (upsellDefinition) {
            nextState.upsell[plan.flatData.alias] = upsellDefinition

            handleDefinitionGet(
              currentUpsellDefinition,
              nextState.upsell[plan.flatData.alias]
            )
          }
        }

        nextState.loaded = true
        return
      }

      case BonusActions.AX_DRAWER_TOGGLE_UPSELL: {
        const upsell = nextState.upsell[action.alias]

        if (!isEmpty(upsell)) {
          upsell.isSelectedInDrawer = !upsell.isSelectedInDrawer
        }

        nextState.loaded = true
        return
      }

      case BonusActions.AX_DRAWER_CLOSE: {
        nextState.axDrawer = undefined
        nextState.product = undefined
        nextState.upsell = {}
        nextState.loaded = false
        return
      }

      case BonusActions.MINI_CART_OPEN:
        nextState.miniCartOpen = true
        return

      case BonusActions.MINI_CART_CLOSE:
        nextState.miniCartOpen = false
        return

      case BonusActions.BACK_TO_SHOP_URL_SET:
        nextState.backToShopUrl = action.shopUrl
        return

      default:
        break
    }
  })
}
